import React, { useCallback, useEffect, useState } from "react";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "credit-mabligop.theme";
const THEME_EVENT = "credit-mabligop:theme-change";

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Storage is optional; system mode remains a safe fallback.
  }
  return "system";
}

function systemIsDark() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

export function applyTheme(preference: ThemePreference) {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(preference, systemIsDark());
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

export function setThemePreference(preference: ThemePreference) {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // The active document can still change appearance without persistence.
  }
  applyTheme(preference);
  window.dispatchEvent(new CustomEvent<ThemePreference>(THEME_EVENT, { detail: preference }));
}

export function useThemePreference() {
  const [preference, setPreference] = useState<ThemePreference>(readThemePreference);

  useEffect(() => {
    applyTheme(preference);
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => preference === "system" && applyTheme("system");
    const onThemeChange = (event: Event) => setPreference((event as CustomEvent<ThemePreference>).detail);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = event.newValue;
      if (next === "light" || next === "dark" || next === "system") setPreference(next);
    };
    media.addEventListener("change", onSystemChange);
    window.addEventListener(THEME_EVENT, onThemeChange);
    window.addEventListener("storage", onStorage);
    return () => {
      media.removeEventListener("change", onSystemChange);
      window.removeEventListener(THEME_EVENT, onThemeChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [preference]);

  const changeTheme = useCallback((next: ThemePreference) => {
    setPreference(next);
    setThemePreference(next);
  }, []);

  return { preference, setPreference: changeTheme };
}

export function AppearanceSwitcher({ compact = false }: { compact?: boolean }) {
  const { preference, setPreference } = useThemePreference();
  const items: { id: ThemePreference; icon: string; label: string }[] = [
    { id: "system", icon: "◐", label: "System" },
    { id: "light", icon: "☼", label: "Light" },
    { id: "dark", icon: "☾", label: "Dark" },
  ];
  return (
    <div className={`appearance-switcher ${compact ? "compact" : ""}`} role="group" aria-label="Appearance">
      {items.map((item) => (
        <button key={item.id} type="button" className={preference === item.id ? "active" : ""} aria-pressed={preference === item.id} title={item.label} onClick={() => setPreference(item.id)}>
          <span aria-hidden="true">{item.icon}</span>{compact ? null : item.label}
        </button>
      ))}
    </div>
  );
}
